import { isRunningInMetaOSHub } from 'owa-config';
import { lazyGetContext } from 'owa-metaos-app-bootstrap';
import { markFunction } from 'owa-performance';
import { placesSessionDataTable } from './sessionDataDatabase';

import type { SessionData } from 'owa-service/lib/types/SessionData';
import type { TraceErrorObject } from 'owa-analytics-types';

// Core logic extracted from packages/libraries/worker/owa-data-worker-client/src/actions/getOfflineSessionData.ts
// Tweaked to use metaos context and Places' indexDB
export const getOfflineSessionData: (
    metaOsUserId?: string | undefined,
    metaOsTenantId?: string | undefined
) => Promise<SessionData> = markFunction(getSessionData, 'offsd');

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

async function getSessionData(
    metaOsUserId?: string,
    metaOsTenantId?: string
): Promise<SessionData> {
    if (!isRunningInMetaOSHub()) {
        const error: TraceErrorObject = new Error('Not running in MetaOS Hub');
        error.additionalInfo = { metaOsUserId, metaOsTenantId };
        throw error;
    }

    if (!metaOsUserId || !metaOsTenantId) {
        const metaOSContext = await lazyGetContext.importAndExecute();
        metaOsUserId = metaOSContext?.user?.id;
        metaOsTenantId = metaOSContext?.user?.tenant?.id;
    }

    if (!metaOsUserId || !metaOsTenantId) {
        const error: TraceErrorObject = new Error('MetaOS context is not available');
        error.additionalInfo = { metaOsUserId, metaOsTenantId };
        throw error;
    }

    const ownerId = `${metaOsUserId}@${metaOsTenantId}`;

    const placesSessionData = await placesSessionDataTable.get({ ownerId });

    if (!placesSessionData?.sessionData) {
        // If we don't have session data, we should reject the promise
        // to make sure that network session data wins
        throw new Error('Could not load session data from Places offline store');
    }

    const cachedTimestamp = placesSessionData?.timestamp ?? 0;
    const currentTimestamp = Date.now();
    const isSessionDataStale = cachedTimestamp < currentTimestamp - SEVEN_DAYS;
    if (placesSessionData?.sessionData && isSessionDataStale) {
        const error: TraceErrorObject = new Error('Session data is stale. exceeded 7 days.');
        error.additionalInfo = { metaOsUserId, metaOsTenantId, cachedTimestamp, currentTimestamp };
        throw error;
    }

    return {
        isOffline: true,
        ...placesSessionData.sessionData,
    };
}
